<script setup lang="ts">
import {
  PhArrowLeft as ArrowLeft,
  PhArrowRight as ArrowRight,
  PhArrowClockwise as Refresh,
  PhCheckCircle as CheckCircle,
  PhLeaf as Leaf,
  PhQrCode as QrCode,
} from "@phosphor-icons/vue";
import { computed, onMounted, ref } from "vue";
import BrandBar from "./BrandBar.vue";
import HomeButton from "./HomeButton.vue";
import { stateQuestions } from "../data/content";
import { loadWecomContactWay, WecomContactWayError } from "../lib/wecom-contact";
import { useSessionStore } from "../stores/session";

const store = useSessionStore();
const qrCode = ref("");
const qrLoading = ref(true);
const qrError = ref("");

const focusLabel = computed(() => (
  stateQuestions[0].options.find(([value]) => value === store.stateAnswers.S01)?.[1] || "烦躁内耗"
));
const timeLabel = computed(() => (
  stateQuestions[1].options.find(([value]) => value === store.stateAnswers.S02)?.[1] || "5 分钟"
));

const daySteps = [
  {
    day: "DAY 1",
    title: "今天先做什么",
    detail: "领取今天的具体行动，马上就能开始",
    image: "/assets/lead-day-1.webp",
    tone: "green",
  },
  {
    day: "DAY 2",
    title: "继续下一步",
    detail: "根据你的回血方向，继续完成第2个小行动",
    image: "/assets/lead-day-2.webp",
    tone: "yellow",
  },
  {
    day: "DAY 3",
    title: "回看这3天",
    detail: "看看哪一步最适合你，再决定下一步怎么走",
    image: "/assets/lead-day-3.webp",
    tone: "purple",
  },
];

async function loadQrCode() {
  qrLoading.value = true;
  qrError.value = "";
  try {
    const result = await loadWecomContactWay(store.identity);
    qrCode.value = result.qr_code;
    store.track("contact_qr_loaded", { status: result.status });
  } catch (reason) {
    const code = reason instanceof WecomContactWayError ? reason.code : "network_error";
    qrCode.value = "";
    qrError.value = code === "wecom_contact_not_configured"
      ? "专属二维码服务尚未配置，请稍后再试"
      : "专属二维码暂时生成失败，请重新获取";
    store.track("contact_qr_failed", { error: code });
  } finally {
    qrLoading.value = false;
  }
}

onMounted(() => void loadQrCode());
</script>

<template>
  <section class="screen screen--lead" :data-user-ref="store.identity?.userRef || undefined">
    <header class="subpage-header lead-page-header">
      <button class="icon-button" aria-label="返回方案" @click="store.go('plan')"><ArrowLeft :size="22" weight="bold" /></button>
      <BrandBar compact label="工位回血方案" />
      <HomeButton />
    </header>

    <section class="lead-hero">
      <div class="lead-hero__content">
        <h1>你的 3 天工位回血计划</h1>
        <p class="lead-hero__handwriting">已经准备好了</p>

        <div class="lead-choice-summary">
          <span>你刚刚最想改善的是 <strong>{{ focusLabel }}</strong></span>
          <span>每天愿意给自己 <strong>{{ timeLabel }}</strong></span>
        </div>

        <p class="lead-hero__intro">
          接下来 3 天，不用再自己琢磨今天该做什么。<br />
          每天给你 1 个具体行动，照着做就行。
        </p>
      </div>
    </section>

    <section class="lead-days" aria-labelledby="lead-days-title">
      <h2 id="lead-days-title"><Leaf :size="17" weight="fill" />3 天，你会这样开始<Leaf :size="17" weight="fill" /></h2>
      <ol>
        <li v-for="step in daySteps" :key="step.day" :class="`lead-day-card lead-day-card--${step.tone}`">
          <span class="lead-day-card__day">{{ step.day }}</span>
          <strong>{{ step.title }}</strong>
          <img class="lead-day-card__image" :src="step.image" alt="" />
          <p>{{ step.detail }}</p>
        </li>
      </ol>
    </section>

    <section class="lead-claim" aria-labelledby="lead-claim-title">
      <span class="lead-claim__ribbon">先试 3 天</span>
      <h2 id="lead-claim-title">扫码添加轻养师，<mark>领取 DAY 1</mark></h2>

      <div class="lead-claim__body">
        <div class="lead-claim__copy">
          <p><CheckCircle :size="20" weight="fill" /><span>每天只花 <strong>【{{ timeLabel }}】</strong>，先把最想改善的这件事认真做 3 天。</span></p>
          <p><CheckCircle :size="20" weight="fill" /><span>自愿参加，不影响你已经获得的 NBTI 人格结果和今日工位回血方案。</span></p>
        </div>

        <div class="lead-claim__qr">
          <div v-if="qrLoading" class="contact-qr-state" aria-live="polite" aria-busy="true">
            <Refresh :size="30" class="contact-qr-spinner" />
            <strong>正在生成专属二维码</strong>
            <small>正在关联你的最新测试结果</small>
          </div>
          <img v-else-if="qrCode" :src="qrCode" alt="添加轻养师的专属企业微信二维码" />
          <div v-else class="contact-qr-state contact-qr-state--error" role="alert">
            <QrCode :size="32" />
            <strong>{{ qrError }}</strong>
            <button type="button" @click="loadQrCode"><Refresh :size="16" />重新获取二维码</button>
          </div>
          <small v-if="qrCode">微信扫码，即刻开始</small>
        </div>
      </div>
    </section>

    <button class="lead-return-button" @click="store.go('result')">
      暂不添加，返回人格结果 <ArrowRight :size="18" weight="bold" />
    </button>
  </section>
</template>
